import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Box, Card, CardContent, Typography, Chip, LinearProgress, Grid, Alert, Skeleton, Stack, Tooltip } from '@mui/material';
import { useDataProvider, useNotify } from 'react-admin';
import { AIScoringSectionProps, ExtendedDataProvider, AIScoring, PrepareDataResponse, MarketVelocityFeatures, MLData, MappingComponentScores } from '../utils/SingleEventAnalysisTab.types';
import FactorsDisplay from '../FactorsDisplay';
import { AISingleEventAnalysis, EventHistoryDashboard, EventItem } from '../../../models/event';
import { Speed } from '@mui/icons-material';

const REAL_TICKET_API_URL = process.env.REAL_TICKET_API_URL || 'https://localhost:5002';

/**
 * Ref handle methods exposed to parent component
 */
export interface ScoringSectionHandle {
    fetchScoringData: (event: EventItem) => Promise<AIScoring>;
    fetchHistoricalScoringData: (analysisId: number | bigint, target?: EventHistoryDashboard) => Promise<AIScoring>;
}

/**
 * ScoringSection Component
 *
 * Smart component that independently fetches and displays AI scoring data for events.
 * Handles its own data fetching lifecycle via useEffect and manages internal loading states.
 *
 * Features:
 * - Independent data fetching on event selection
 * - Loading state management with skeleton UI
 * - Overall score display with progress bar
 * - Profitability and risk assessment metrics
 * - Positive and risk factors display
 * - Recommendation information
 *
 * @param props - Component props containing event data and display functions
 * @param ref - React ref for exposing imperative methods to parent
 * @returns AI scoring analysis section
 */
const ScoringSection = forwardRef<ScoringSectionHandle, AIScoringSectionProps>((props, ref) => {
    const {
        aiScoring,
        setAiScoring,
        getRecommendationIcon,
        getScoreColor
    } = props;

    const [isScoringLoading, setIsScoringLoading] = useState(false);
    const dataProvider = useDataProvider() as unknown as ExtendedDataProvider;
    const [messageError, setMessageError] = useState<string | null>(null);
    const [velocitySnapshot, setVelocitySnapshot] = useState<MarketVelocityFeatures | null>(null);
    const notify = useNotify();

    // Initialize velocitySnapshot from aiScoring.velocityData on mount/re-render
    React.useEffect(() => {
        if (aiScoring?.velocityData) {
            // Ensure velocityData has component scores calculated and with right format
            let velocityDataWithScores = MappingComponentScores(aiScoring.velocityData);
            setVelocitySnapshot(velocityDataWithScores);
        }
    }, [aiScoring?.velocityData]);

    /**
     * Fetches scoring data for the current event
     */
    const fetchScoringData = async (event: EventItem): Promise<AIScoring> => {
        // get username
        const username = await dataProvider.currentUser<any>();
        setIsScoringLoading(true);
        try {
            setVelocitySnapshot(null);
            const prepareResp = await dataProvider.postData<PrepareDataResponse>('RTP_Event', 'PrepareScoreEvent', EventItem.toRawEventData(event));
            // PrepareScoreEvent now returns RTP_EventInputDto directly from the backend
            // No need for frontend mapping - the backend handles this via BulkAnalysisService
            const { eventData, message } = prepareResp.data;
            // Check if the response contains a warning message (e.g., ignored status event or no data found)
            if (message) {
                setMessageError(message);
                setAiScoring(null);
                return {} as AIScoring;
            }
            // Fetch velocity snapshot before setting prepare data
            let velocityData: MarketVelocityFeatures | null = null;
            try {
                const velocityPayload = {
                    targetDate: null, // Use current date
                    items: [{
                        siteId: eventData.event?.siteId || eventData?.siteId || '',
                        venueName: eventData.event?.venue  || eventData?.venueName || '',
                        eventDate: eventData.eventDate || eventData.event?.dateTime || '',
                        onsaleDate: eventData.onsaleDate || '',
                        marketType: eventData.marketType || '',
                        venueCapacity: eventData.venueCapacity ?? 0
                    }]
                };
                
                const velocityResponse = await fetch(`${REAL_TICKET_API_URL}/market-data/velocity-snapshot`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(velocityPayload)
                });
                
                if (velocityResponse.ok) {
                    const velocityResult = await velocityResponse.json();
                    if (velocityResult.results && velocityResult.results.length > 0 && velocityResult.results[0].success) {
                        let features = velocityResult.results[0].features;
                        features && (features.timestamp = velocityResult.timestamp);
                        velocityData = features;
                        setVelocitySnapshot(MappingComponentScores(features)); // mapping component scores for display tooltips,text,...
                    }
                }
            } catch (velocityError) {
                // Log error but don't fail the entire scoring process
                console.warn('Failed to fetch velocity snapshot:', velocityError);
            }

            // Merge velocity data (original data) with eventData before sending to score-event
            const eventDataWithVelocity = velocityData
                ? { ...eventData, velocitySnapshot: velocityData }
                : eventData;

            const response = await fetch(`${REAL_TICKET_API_URL}/score-event`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ eventData: eventDataWithVelocity, username: username?.email || 'unknown' })
            });
            if (!response.ok) {
                const respError = await response.json();
                throw new Error(`Real Ticket API error: ${response.status} ${respError.details}`);
            }
            const scoringData = await response.json() as AIScoring;
            scoringData.prepareData = eventData;
            // Persist velocityData (original data) in scoringData to prevent data loss
            scoringData.velocityData = velocityData;
            setAiScoring(scoringData);
            return scoringData;
        } catch (error) {
            notify(`Error fetching scoring data: ${error instanceof Error ? error.message : 'Unknown error'}`, { type: 'error' });
            throw error;
        } finally {
            setIsScoringLoading(false);
        }
    };
    const fetchHistoricalScoringData = async (analysisId: number | bigint, target?: EventHistoryDashboard): Promise<AIScoring> => {
        setIsScoringLoading(true);
        try {
            const singleEventAnalysis = await dataProvider.fetchData<AISingleEventAnalysis>('HistoryAnalysis', `GetSingleEventAnalysis?analysisId=${analysisId}`);

            if (!singleEventAnalysis || !singleEventAnalysis.data) {
                console.warn('No analyzed event found');
                // return empty AIScoring
                return {} as AIScoring;
            }
            // convert AISingleEventAnalysis to AIScoring
            const aiScoringHistory = AIScoring.fromSingleEventAnalysis(singleEventAnalysis.data);
            if (target) {
                aiScoringHistory.mlData = {
                    eventQualityScore: target.mL_Score,
                    summaryDetails: {
                        recommendationCounts: {
                            buy: target.mL_BuyCount,
                            strongBuy: target.mL_StrongBuyCount
                        }
                    }
                };
            }
            setAiScoring(aiScoringHistory);
            // Get velocity snapshot from aiScoringHistory.featuresJson if available
            if (aiScoringHistory.featuresJson) {
                try {
                    const features = JSON.parse(aiScoringHistory.featuresJson);
                    if (features.velocitySnapshot) {
                        const velocityData: MarketVelocityFeatures = features.velocitySnapshot;
                        let velocityDataWithScores = MappingComponentScores(velocityData);
                        setVelocitySnapshot(velocityDataWithScores);
                    }
                } catch (parseError) {
                    console.warn('Failed to parse featuresJson for velocity snapshot:', parseError);
                }
            }
                        
            return aiScoringHistory;
        } catch (error) {
            notify(`Error fetching scoring data: ${error instanceof Error ? error.message : 'Unknown error'}`, { type: 'error' });
            throw error;
        } finally {
            setIsScoringLoading(false);
        }
    };

    useImperativeHandle(ref, () => ({
        fetchScoringData,
        fetchHistoricalScoringData
    }));

    const renderMLScore = (mlData: MLData | null) => {
        if (mlData === null || !mlData) return null;
        const mlScore = mlData?.eventQualityScore;
        const mlBuyCount = mlData.summaryDetails.recommendationCounts?.buy;
        const mlStrongBuyCount = mlData.summaryDetails.recommendationCounts?.strongBuy;
    
        // Build the display text
        const parts: string[] = [];
        const counts: string[] = [];
        if (mlBuyCount && mlBuyCount > 0) {
            counts.push(`Buy: ${mlBuyCount}`);
        }
        if (mlStrongBuyCount && mlStrongBuyCount > 0) {
            counts.push(`StrongBuy: ${mlStrongBuyCount}`);
        }
            
        if (counts.length > 0) {
            parts.push(`(${counts.join(', ')})`);
        }
    
        const displayText = parts.join(' ');
    
        return mlScore?.toFixed(1) + (displayText ? ` ${displayText}` : '');
    };
    return (
        <Grid size={4}>
            <Card>
                <CardContent>
                    {isScoringLoading ? (
                        // Show skeleton placeholder while AI Scoring data is loading
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Skeleton variant="text" width="60%" height={32} />
                                <Box sx={{ flexGrow: 1 }} />
                                <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 2 }} />
                            </Box>
                            <Skeleton variant="text" width="40%" height={24} sx={{ mb: 1 }} />
                            <Skeleton variant="rectangular" width="100%" height={10} sx={{ borderRadius: 5, mb: 3 }} />
                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <Skeleton variant="text" width="80%" height={20} />
                                    <Skeleton variant="text" width="60%" height={32} />
                                </Grid>
                                <Grid size={6}>
                                    <Skeleton variant="text" width="80%" height={20} />
                                    <Skeleton variant="text" width="60%" height={32} />
                                </Grid>
                            </Grid>
                            <Skeleton variant="rectangular" width="100%" height={100} sx={{ mt: 2, borderRadius: 1 }} />
                            <Skeleton variant="rectangular" width="100%" height={80} sx={{ mt: 2, borderRadius: 1 }} />
                            <Skeleton variant="rectangular" width="100%" height={40} sx={{ mt: 2, borderRadius: 1 }} />
                            <Skeleton variant="rectangular" width="100%" height={80} sx={{ mt: 2, borderRadius: 1 }} />
                        </>
                    ) : aiScoring ? (
                        // Show actual AI Scoring content when data is available
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                    AI Event Analysis
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {getRecommendationIcon((aiScoring.recommendationLevel || (aiScoring.recommendation && aiScoring.recommendation.decision)) || '')}
                                    <Chip
                                        label={aiScoring.recommendationLevel || (aiScoring.recommendation && aiScoring.recommendation.decision)}
                                        color={getScoreColor(aiScoring.overallEventScore || (aiScoring.scores?.overallScore ?? 0))}
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Overall Score: {(aiScoring.scores?.overallScore || 0).toFixed(1)}
                                    </Typography>
                                    {!!aiScoring.mlData && !!aiScoring.mlData.eventQualityScore && (
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                            ML Score: {renderMLScore(aiScoring.mlData)}
                                        </Typography>
                                    )}
                                </Box>
                                
                                <LinearProgress
                                    variant="determinate"
                                    value={aiScoring.scores?.overallScore || 0}
                                    sx={{
                                        height: 10,
                                        borderRadius: 5,
                                        backgroundColor: 'grey.200'
                                    }}
                                />
                            </Box>

                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <Typography variant="caption" color="textSecondary">
                                        Profitability Prediction
                                    </Typography>
                                    <Typography variant="h6" color="success.main">
                                        {(aiScoring.scores?.totalPositiveScore || 0).toFixed(1)}%
                                    </Typography>
                                </Grid>
                                <Grid size={6}>
                                    <Typography variant="caption" color="textSecondary">
                                        Risk Assessment
                                    </Typography>
                                    <Typography variant="h6" color="warning.main">
                                        {(aiScoring.scores?.totalRiskScore || 0).toFixed(1)}%
                                    </Typography>
                                </Grid>
                            </Grid>

                            {/* Handle both response formats for positive and risk factors */}
                            <FactorsDisplay
                                positiveFactors={aiScoring.positiveFactors || aiScoring.factors?.positiveFactors}
                                riskFactors={aiScoring.riskFactors || aiScoring.factors?.riskFactors}
                            />

                            {/* Show recommendation from real-ticket-api format */}
                            {(aiScoring.recommendation || aiScoring.recommendationLevel) && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2">
                                        Recommendation: {aiScoring.recommendation?.decision || aiScoring.recommendationLevel}
                                    </Typography>
                                    <Typography variant="body2">
                                        Confidence: {aiScoring.recommendation?.confidenceLevel || 'Medium'}
                                    </Typography>
                                </Alert>
                            )}
                            
                            {/* Display velocity snapshot data if available */}
                            {velocitySnapshot && (
                                <Box sx={{ mt: 2, p: 2, backgroundColor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} mb={1}>
                                        <Stack direction="row" alignItems="center" gap={1}>
                                            <Speed color="warning" fontSize="small" />
                                            <Typography variant="subtitle2">
                                                Velocity Snapshot
                                            </Typography>
                                        </Stack>
                                        {velocitySnapshot.simplifiedScore !== undefined && velocitySnapshot.simplifiedScore !== null && (
                                            <Typography variant="subtitle2" color="primary">
                                                Simplified Score: ({velocitySnapshot.simplifiedScore})
                                                {velocitySnapshot.liquidity_consistency_score !== undefined && velocitySnapshot.liquidity_consistency_score !== null && (
                                                    <> | LCS: ({Number(velocitySnapshot.liquidity_consistency_score).toFixed(2)})</>
                                                )}
                                            </Typography>
                                        )}
                                    </Stack>
                                    <Box sx={{ pl: 3.5 }}>
                                        <Grid container spacing={1}>
                                            {velocitySnapshot.days_until_event !== undefined && (
                                                <Grid size={6}>
                                                    <Tooltip
                                                        title={typeof velocitySnapshot.daysScore === 'object' && velocitySnapshot.daysScore?.tooltip ? velocitySnapshot.daysScore.tooltip : ''}
                                                        arrow
                                                        placement="top"
                                                        slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', textAlign: 'left' } } }}
                                                    >
                                                        <Typography variant="caption" color="textSecondary">
                                                            {typeof velocitySnapshot.daysScore === 'object' && velocitySnapshot.daysScore?.text ? velocitySnapshot.daysScore.text : 'Days Until Event'}
                                                            {velocitySnapshot.daysScore !== undefined && <span> (<span style={{ color: '#1976d2', fontWeight: 600 }}>{typeof velocitySnapshot.daysScore === 'object' ? velocitySnapshot.daysScore.score : velocitySnapshot.daysScore}</span>)</span>}
                                                        </Typography>
                                                    </Tooltip>
                                                    <Typography variant="body2">{velocitySnapshot.days_until_event}</Typography>
                                                </Grid>
                                            )}
                                            {velocitySnapshot.days_since_onsale !== undefined && (
                                                <Grid size={6}>
                                                    <Typography variant="caption" color="textSecondary">Days Since Onsale</Typography>
                                                    <Typography variant="body2">{velocitySnapshot.days_since_onsale}</Typography>
                                                </Grid>
                                            )}
                                            {velocitySnapshot.primary_days_to_sellout !== undefined && (
                                                <Grid size={6}>
                                                    <Typography variant="caption" color="textSecondary">Primary Days to Sellout</Typography>
                                                    <Typography variant="body2">{typeof velocitySnapshot.primary_days_to_sellout === 'number' ? velocitySnapshot.primary_days_to_sellout.toFixed(2) : velocitySnapshot.primary_days_to_sellout}</Typography>
                                                </Grid>
                                            )}
                                            {velocitySnapshot.days_before_event_sells_out !== undefined && (
                                                <Grid size={6}>
                                                    <Tooltip
                                                        title={typeof velocitySnapshot.primaryScore === 'object' && velocitySnapshot.primaryScore?.tooltip ? velocitySnapshot.primaryScore.tooltip : ''}
                                                        arrow
                                                        placement="top"
                                                        slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', textAlign: 'left' } } }}
                                                    >
                                                        <Typography variant="caption" color="textSecondary">
                                                            {typeof velocitySnapshot.primaryScore === 'object' && velocitySnapshot.primaryScore?.text ? velocitySnapshot.primaryScore.text : 'Days until Event - Primary Days to Sellout'}
                                                            {velocitySnapshot.primaryScore !== undefined && <span> (<span style={{ color: '#1976d2', fontWeight: 600 }}>{typeof velocitySnapshot.primaryScore === 'object' ? velocitySnapshot.primaryScore.score : velocitySnapshot.primaryScore}</span>)</span>}
                                                        </Typography>
                                                    </Tooltip>
                                                    <Typography variant="body2">{typeof velocitySnapshot.days_before_event_sells_out === 'number' ? velocitySnapshot.days_before_event_sells_out.toFixed(2) : velocitySnapshot.days_before_event_sells_out}</Typography>
                                                </Grid>
                                            )}
                                            {velocitySnapshot.tn_two_weeks_ago_number_of_sales !== undefined && (
                                                <Grid size={6}>
                                                    <Typography variant="caption" color="textSecondary">Total TN sales for this event/venue last 14 days</Typography>
                                                    <Typography variant="body2">{velocitySnapshot.tn_two_weeks_ago_number_of_sales}</Typography>
                                                </Grid>
                                            )}
                                            {velocitySnapshot.tn_all_time_number_of_sales !== undefined && (
                                                <Grid size={6}>
                                                    <Typography variant="caption" color="textSecondary">Total TN sales for this event/venue</Typography>
                                                    <Typography variant="body2">{velocitySnapshot.tn_all_time_number_of_sales}</Typography>
                                                </Grid>
                                            )}
                                            {velocitySnapshot.total_spec_sales !== undefined && (
                                                <Grid size={6}>
                                                    <Typography variant="caption" color="textSecondary">Total Song Spec Sales at venue (past year)</Typography>
                                                    <Typography variant="body2">{velocitySnapshot.total_spec_sales}</Typography>
                                                </Grid>
                                            )}
                                            {velocitySnapshot.total_tn_sales !== undefined && (
                                                <Grid size={6}>
                                                    <Typography variant="caption" color="textSecondary">Total TN Sales at Venue (past year)</Typography>
                                                    <Typography variant="body2">{velocitySnapshot.total_tn_sales}</Typography>
                                                </Grid>
                                            )}
                                            {velocitySnapshot.venue_total_sales !== undefined && (
                                                <Grid size={6}>
                                                    <Tooltip
                                                        title={typeof velocitySnapshot.venueScore === 'object' && velocitySnapshot.venueScore?.tooltip ? velocitySnapshot.venueScore.tooltip : ''}
                                                        arrow
                                                        placement="top"
                                                        slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', textAlign: 'left' } } }}
                                                    >
                                                        <Typography variant="caption" color="textSecondary">
                                                            {typeof velocitySnapshot.venueScore === 'object' && velocitySnapshot.venueScore?.text ? velocitySnapshot.venueScore.text : 'Venue Total Sales (Spec + TN total)'}
                                                            {velocitySnapshot.venueScore !== undefined && <span> (<span style={{ color: '#1976d2', fontWeight: 600 }}>{typeof velocitySnapshot.venueScore === 'object' ? velocitySnapshot.venueScore.score : velocitySnapshot.venueScore}</span>)</span>}
                                                        </Typography>
                                                    </Tooltip>
                                                    <Typography variant="body2">{velocitySnapshot.venue_total_sales}</Typography>
                                                </Grid>
                                            )}
                                            {velocitySnapshot.tn_all_time_number_of_sales !== undefined && (
                                                <Grid size={6}>
                                                    <Tooltip
                                                        title={typeof velocitySnapshot.eventScore === 'object' && velocitySnapshot.eventScore?.tooltip ? velocitySnapshot.eventScore.tooltip : ''}
                                                        arrow
                                                        placement="top"
                                                        slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', textAlign: 'left' } } }}
                                                    >
                                                        <Typography variant="caption" color="textSecondary">
                                                            {typeof velocitySnapshot.eventScore === 'object' && velocitySnapshot.eventScore?.text ? velocitySnapshot.eventScore.text : 'TN Sales for Event + Song Spec Sales for Event'}
                                                            {velocitySnapshot.eventScore !== undefined && <span> (<span style={{ color: '#1976d2', fontWeight: 600 }}>{typeof velocitySnapshot.eventScore === 'object' ? velocitySnapshot.eventScore.score : velocitySnapshot.eventScore}</span>)</span>}
                                                        </Typography>
                                                    </Tooltip>
                                                    <Typography variant="body2">{velocitySnapshot.tn_all_time_number_of_sales + (velocitySnapshot.total_spec_sales_event ?? 0)}</Typography>
                                                </Grid>
                                            )}
                                            {velocitySnapshot.total_spec_sales_event !== undefined && (
                                                <Grid size={6}>
                                                    <Typography variant="caption" color="textSecondary">Total Song Spec Sales for Event</Typography>
                                                    <Typography variant="body2">{velocitySnapshot.total_spec_sales_event}</Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Box>
                                </Box>
                            )}
                        </>
                    ) : (
                        // Show empty state when no data and not loading
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                AI Event Analysis
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                No analysis data available
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>
            { messageError && (<Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Analysis Results:</Typography>
                <Typography variant="body2">{messageError}</Typography>
            </Alert>)}
        </Grid>
    );
});

ScoringSection.displayName = 'ScoringSection';

export default ScoringSection;
